// Auth Context - Manages admin authentication state
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

// Configure axios to send cookies
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  // null = checking, false = not authenticated, object = authenticated user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on mount
  const checkAuth = useCallback(async () => {
    try {
      // First try to get token from localStorage
      const storedToken = localStorage.getItem('salesspace_admin_token');
      
      const config = storedToken ? {
        headers: { Authorization: `Bearer ${storedToken}` },
        withCredentials: true
      } : { withCredentials: true };
      
      const response = await axios.get(`${API_URL}/api/auth/me`, config);
      
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
      } else {
        setUser(false);
        localStorage.removeItem('salesspace_admin_token');
      }
    } catch (error) {
      setUser(false);
      localStorage.removeItem('salesspace_admin_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login function
  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setUser(response.data.user);
        // Store token in localStorage as backup
        if (response.data.access_token) {
          localStorage.setItem('salesspace_admin_token', response.data.access_token);
        }
        return { success: true };
      }
      return { success: false, error: 'Login failed' };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return { 
        success: false, 
        error: formatApiError(detail) || 'Login failed'
      };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore logout errors
    }
    setUser(false);
    localStorage.removeItem('salesspace_admin_token');
  };

  // Get auth header for API calls
  const getAuthHeader = () => {
    const token = localStorage.getItem('salesspace_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      checkAuth,
      getAuthHeader,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper to format API errors
function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
